import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { format, parse } from 'date-fns'
import { Checkbox, Divider, Grid } from '@material-ui/core'
import * as Yup from 'yup'
import Form from 'shared/components/Form'
import Input from 'shared/components/Input'
import Formatter, { cepMask, cpfMask, phoneMask } from 'shared/utils/formatters'
import { colors } from 'shared/styles/colors'
import Footer from '../components/Footer'
import LoadingBackdrop from 'shared/components/LoadingBackdrop'
import { useOnboarding } from 'modules/pagamentos/hooks/useOnboarding'
import { IConsultZipCode } from 'modules/user/domain/usecases/ConsultZipCodeImpl'
import { EPerfil } from 'modules/pagamentos/domain/entities/EPerfil'
import getValidationErrors from 'shared/utils/getValidationErrors'
import ButtonFile from 'shared/components/ButtonFile'
import ETipoDocumentoOnboarding from 'modules/pagamentos/domain/entities/ETipoDocumentoOnboarding'
import { useFrotista } from 'modules/user/hooks/useFrotista'
import SnackBar from 'shared/components/SnackBar'
import { Dropdown } from 'shared/components/Dropdown'
import KeyboardDatePicker from 'shared/components/KeyboardDatePicker'
import { Subtitle, Title, Text, Ul, Li } from '../styles'
import { IFormControl } from 'shared/components/Form/FormComponent.types'

interface RepresentanteProps {
  consultZipCode: IConsultZipCode
}

interface IFormProps {
  relacao: string
  cpf: string
  nomeCompleto: string
  dataNascimento: Date
  nomeMae: string
  email: string
  celular: string
}

const Representante: FunctionComponent<RepresentanteProps> = ({
  consultZipCode,
}) => {
  const {
    handleBackButton,
    handleNextButton,
    contaPj,
    setContaPj,
    addDocumento,
    getDocumentoNome,
  } = useOnboarding()
  const { frotistaId } = useFrotista()
  const [loading, setLoading] = useState(false)
  const formRef = useRef<IFormControl>()
  const relacaoList = [
    {
      label: 'Sócio',
      value: EPerfil.SOCIO,
    },
    {
      label: 'Procurador/Mandatário',
      value: EPerfil.RESPONSAVEL,
    },
  ]
  const pessoaFisica = contaPj?.responsaveis?.pessoas_fisicas[0]
  const [relacao, setRelacao] = useState<EPerfil | undefined>(
    () => pessoaFisica?.perfil || null,
  )
  const [relacaoInput, setRelacaoInput] = useState(
    () =>
      relacaoList.find(relacao => relacao.value === pessoaFisica?.perfil) || '',
  )
  const [cpf, setCpf] = useState(() => pessoaFisica?.cpf || '')
  const [nomeCompleto, setNomeCompleto] = useState(
    () => pessoaFisica?.nome || '',
  )
  const [dataNascimento, setDataNascimento] = useState<Date | string>(
    () => pessoaFisica?.data_nascimento || null,
  )
  const [nomeMae, setNomeMae] = useState(() =>
    pessoaFisica?.nome_mae !== 'DESCONHECIDO'
      ? pessoaFisica?.nome_mae || ''
      : '',
  )
  const [naoConstaNomeMae, setNaoConstaNomeMae] = useState(
    pessoaFisica?.nome_mae === 'DESCONHECIDO',
  )
  const [email, setEmail] = useState(() => pessoaFisica?.email || '')
  const [celular, setCelular] = useState(() => pessoaFisica?.telefone || '')
  const [cep, setCep] = useState(() => pessoaFisica?.cep || '')
  const [logradouro, setLogradouro] = useState(
    () => pessoaFisica?.logradouro || '',
  )
  const [numero, setNumero] = useState(() => pessoaFisica?.numero || '')
  const [bairro, setBairro] = useState(() => pessoaFisica?.bairro || '')
  const [cidade, setCidade] = useState(() => pessoaFisica?.cidade || '')
  const [cidadeIbge, setCidadeIbge] = useState(
    () => pessoaFisica?.cidade_ibge || '',
  )
  const [estado, setEstado] = useState(() => pessoaFisica?.estado || '')
  const [complemento, setComplemento] = useState(
    () => pessoaFisica?.complemento || '',
  )
  const [isOk, setIsOk] = useState(false)
  const [errorMessageForm, setErrorMessageForm] = useState({} as IFormProps)
  const semErrosDeValidacao = Object.values(errorMessageForm).every(
    errorMessage => errorMessage === '',
  )
  const [openErrorMessage, setOpenErrorMessage] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const handleOnBlur = (field: string, value: any): void => {
    if (!value) {
      setErrorMessageForm(errors => ({
        ...errors,
        [field]: 'Campo obrigatório',
      }))
    } else {
      setErrorMessageForm(errors => ({
        ...errors,
        [field]: '',
      }))
    }

    if (semErrosDeValidacao) setIsOk(true)
    else setIsOk(false)
  }

  const handleCep = useCallback(
    async (value: string) => {
      if (value.replace(Formatter.somenteNumeros(), '')?.length !== 8) return
      setErrorMessage('')
      setOpenErrorMessage(false)
      setLoading(true)
      setCep(value)
      try {
        const schema = Yup.object().shape({
          cep: Yup.string().matches(/^([\d]{5})-*([\d]{3})/),
        })

        await schema.validate(
          {
            cep: value,
          },
          {
            abortEarly: false,
          },
        )
        const response = await consultZipCode.execute({ cep: value })

        if (response.cidade) {
          if (formRef.current) {
            formRef.current.setFieldValue('logradouro', response.logradouro)
            formRef.current.setFieldValue('bairro', response.bairro)
            formRef.current.setFieldValue('cidade', response.cidade)
            formRef.current.setFieldValue('estado', response.estado)
          }
          setCidadeIbge(response.cidade_ibge)
          setLogradouro(response.logradouro)
          setCidade(response.cidade)
          setEstado(response.estado)
          setBairro(response.bairro)
          setContaPj({
            ...contaPj,
            responsaveis: {
              ...contaPj.responsaveis,
              pessoas_fisicas: [
                {
                  ...contaPj?.responsaveis?.pessoas_fisicas[0],
                  logradouro: response.logradouro,
                  bairro: response.bairro,
                  cep: value,
                  cidade_ibge: response.cidade_ibge,
                  cidade: response.cidade,
                  estado: response.estado,
                } as any,
              ],
            },
          })
        } else {
          setErrorMessage('Cep inválido.')
          setOpenErrorMessage(true)
        }
      } catch (e: any) {
        setErrorMessage(e.message)
        setOpenErrorMessage(true)
      } finally {
        setLoading(false)
      }
    },
    [consultZipCode, cep],
  )

  const validateForm = async (isSubmit = false) => {
    try {
      const celularFormatado = celular.replace(Formatter.somenteNumeros(), '')
      const schema = Yup.object().shape({
        relacao: Yup.string().required('Relação é obrigatória!'),
        cpf: Yup.string().matches(
          /^(([0-9]{3}.[0-9]{3}.[0-9]{3}-[0-9]{2})|([0-9]{11}))$/,
          'CPF inválido!',
        ),
        nomeCompleto: Yup.string().required('Nome é obrigatório!'),
        dataNascimento: Yup.date().required(
          'Data de nascimento é obrigatória!',
        ),
        nomeMae: naoConstaNomeMae
          ? Yup.string().optional()
          : Yup.string().required('Nome mãe é obrigatório!'),
        email: Yup.string()
          .email('Email inválido!')
          .required('Email é obrigatório!'),
        celular: Yup.string()
          .trim()
          .required('Celular é obrigatório!')
          .min(10, 'Celular inválido!')
          .max(11, 'Celular inválido!'),
        cep: Yup.string().required('CEP inválido!'),
        logradouro: Yup.string().required('Logradouro inválido!'),
        numero: Yup.string().required('Número inválido!'),
        bairro: Yup.string().required('Bairro inválido!'),
        cidade: Yup.string().required('Cidade inválido!'),
        estado: Yup.string().required('Estado inválido!'),
        complemento: Yup.string().optional(),
      })
      await schema.validate(
        {
          relacao,
          cpf,
          nomeCompleto,
          dataNascimento:
            typeof dataNascimento === 'string'
              ? parse(dataNascimento, 'dd/MM/yyyy', new Date())
              : dataNascimento,
          nomeMae,
          email,
          celular: celularFormatado,
          cep,
          logradouro,
          numero,
          bairro,
          cidade,
          estado,
          complemento,
        },
        {
          abortEarly: false,
        },
      )
      setIsOk(true)
    } catch (error: any) {
      setIsOk(false)
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationErrors(error)
        setErrorMessage(errors.message)
      } else {
        setErrorMessage(error.message)
      }
      setErrorMessage(error.message)
      if (isSubmit) setOpenErrorMessage(true)
    }
  }

  useEffect(() => {
    const load = async () => {
      await handleCep(cep)
      validateForm()
    }
    load()
  }, [])

  const handleSubmit = async () => {
    if (
      !getDocumentoNome(ETipoDocumentoOnboarding.SELFIE) ||
      !getDocumentoNome(ETipoDocumentoOnboarding.CNH)
    ) {
      setErrorMessage('Anexar SELFIE e CNH')
      setOpenErrorMessage(true)
      return
    }
    try {
      setErrorMessage('')
      setOpenErrorMessage(false)
      const celularFormatado = celular.replace(Formatter.somenteNumeros(), '')
      validateForm(true)

      setContaPj({
        ...contaPj,
        responsaveis: {
          ...contaPj.responsaveis,
          pessoas_fisicas: [
            {
              cpf: cpf.replace(Formatter.somenteNumeros(), ''),
              nome: nomeCompleto,
              data_nascimento:
                typeof dataNascimento === 'string'
                  ? dataNascimento
                  : format(dataNascimento, 'dd/MM/yyyy'),
              nome_mae: naoConstaNomeMae ? 'DESCONHECIDO' : nomeMae,
              email: email,
              telefone: celularFormatado,
              cep: cep,
              logradouro: logradouro,
              numero: Number(numero),
              bairro: bairro,
              cidade_ibge: Number(cidadeIbge),
              cidade,
              estado,
              complemento: complemento,
              perfil: relacao,
              pessoa_exposta_politicamente: false,
              is_principal: true,
            },
          ],
        },
      })
      handleNextButton()
    } catch (error: any) {
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationErrors(error)
        setErrorMessage(errors.message)
      }
      setErrorMessage(error.message)
      setOpenErrorMessage(true)
    }
  }

  const handleSelectFile = useCallback(
    (file: File, fileName: string, tipo: ETipoDocumentoOnboarding) => {
      addDocumento({
        arquivo: file,
        tipo,
        frotista_id: frotistaId,
      })
    },
    [],
  )

  return (
    <>
      {loading && <LoadingBackdrop open={loading} color={colors.green} />}
      <Divider style={{ margin: '32px 0' }} />
      <Grid container>
        <Grid item xs={12} sm={12} md={4} lg={4}>
          <Title>Dados do representante</Title>
          <Subtitle>Informe os dados do representante</Subtitle>
        </Grid>
        <Form
          onSubmit={undefined}
          innerRef={formRef}
          initialValues={{
            relacao,
            cpf,
            nomeCompleto,
            dataNascimento,
            nomeMae,
            email,
            celular,
            cep,
            logradouro,
            numero,
            bairro,
            cidade,
            estado,
            complemento,
          }}
          validationSchema={() => ({
            relacao: Yup.string().required('Relação é obrigatória!'),
            cpf: Yup.string().matches(
              /^(([0-9]{3}.[0-9]{3}.[0-9]{3}-[0-9]{2})|([0-9]{11}))$/,
              'CPF inválido!',
            ),
            nomeCompleto: Yup.string().required('Nome é obrigatório!'),
            dataNascimento: Yup.date().required(
              'Data de nascimento é obrigatória!',
            ),
            nomeMae: naoConstaNomeMae
              ? Yup.string().optional()
              : Yup.string().required('Nome mãe é obrigatório!'),
            email: Yup.string().email('Email inválido!').required(),
            celular: Yup.string()
              .trim()
              .required('Celular é obrigatório!')
              .min(13, 'Celular inválido!')
              .max(15, 'Celular inválido!'),
            cep: Yup.string().required('CEP inválido!'),
            logradouro: Yup.string().required('Logradouro inválido!'),
            numero: Yup.string().required('Número inválido!'),
            bairro: Yup.string().required('Bairro inválido!'),
            cidade: Yup.string().required('Cidade inválido!'),
            estado: Yup.string().required('Estado inválido!'),
            complemento: Yup.string().optional(),
          })}
        >
          {formControl => (
            <Grid container>
              <Grid container spacing={2} style={{ margin: '8px 0' }}>
                <Grid item xs={12} sm={12} md={5} lg={5}>
                  <Dropdown
                    {...formControl}
                    required={true}
                    placeholder="Relação com a empresa"
                    value={relacaoInput}
                    options={relacaoList}
                    onChange={newValue => {
                      setRelacaoInput(newValue)
                      setRelacao(newValue.value)
                      handleOnBlur('relacao', newValue)
                      setContaPj({
                        ...contaPj,
                        responsaveis: {
                          ...contaPj.responsaveis,
                          pessoas_fisicas: [
                            {
                              ...contaPj?.responsaveis?.pessoas_fisicas[0],
                              perfil: newValue.value,
                            } as any,
                          ],
                        },
                      })
                    }}
                    onBlur={() => {
                      handleOnBlur('relacao', relacao)
                    }}
                    errorMessage={errorMessageForm.relacao}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} style={{ margin: '8px 0' }}>
                <Grid item xs={12} sm={12} md={3} lg={3}>
                  <Input
                    placeholder="Informe o CPF*"
                    name="cpf"
                    type="text"
                    mask={value => cpfMask(value)}
                    maxLength={14}
                    formControl={formControl}
                    onFocus={() => handleOnBlur('relacao', relacao)}
                    value={cpf}
                    onPaste={e => {
                      setCpf(
                        e.target.value?.replace(
                          (Formatter.somenteNumeros(), ''),
                        ),
                      )
                    }}
                    onBlur={() => {
                      handleOnBlur('cpf', formControl.values.cpf)
                      handleOnBlur('relacao', relacao)
                    }}
                    errorMessage={errorMessageForm.cpf}
                    onChange={e => {
                      setCpf(e.target.value)
                      setContaPj({
                        ...contaPj,
                        responsaveis: {
                          ...contaPj.responsaveis,
                          pessoas_fisicas: [
                            {
                              ...contaPj?.responsaveis?.pessoas_fisicas[0],
                              cpf: e.target.value?.replace(
                                Formatter.somenteNumeros(),
                                '',
                              ),
                            } as any,
                          ],
                        },
                      })
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={5} lg={5}>
                  <Input
                    placeholder="Nome completo*"
                    name="nomeCompleto"
                    type="text"
                    formControl={formControl}
                    onBlur={() =>
                      handleOnBlur(
                        'nomeCompleto',
                        formControl.values.nomeCompleto,
                      )
                    }
                    value={nomeCompleto}
                    errorMessage={errorMessageForm.nomeCompleto}
                    onChange={e => {
                      setNomeCompleto(e.target.value)
                      setContaPj({
                        ...contaPj,
                        responsaveis: {
                          ...contaPj.responsaveis,
                          pessoas_fisicas: [
                            {
                              ...contaPj?.responsaveis?.pessoas_fisicas[0],
                              nome: e.target.value,
                            } as any,
                          ],
                        },
                      })
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={3} lg={3}>
                  <KeyboardDatePicker
                    inputVariant="standard"
                    label="Data de nascimento*"
                    selectedDate={
                      typeof dataNascimento === 'string'
                        ? parse(dataNascimento, 'dd/MM/yyyy', new Date())
                        : dataNascimento
                    }
                    handleDateChange={(date: Date) => {
                      setDataNascimento(date)
                      handleOnBlur('dataNascimento', date)
                      setContaPj({
                        ...contaPj,
                        responsaveis: {
                          ...contaPj.responsaveis,
                          pessoas_fisicas: [
                            {
                              ...contaPj?.responsaveis?.pessoas_fisicas[0],
                              data_nascimento: date,
                            } as any,
                          ],
                        },
                      })
                    }}
                    autoOk={true}
                    onBlur={e => handleOnBlur('dataNascimento', e.target.value)}
                    errorMessage={errorMessageForm.dataNascimento?.toString()}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} style={{ margin: '8px 0' }}>
                <Grid item xs={12} sm={12} md={3} lg={3}>
                  <Input
                    placeholder="Nome da mãe*"
                    name="nomeMae"
                    type="text"
                    formControl={formControl}
                    errorMessage={errorMessageForm.nomeMae}
                    value={nomeMae}
                    disabled={naoConstaNomeMae}
                    onBlur={() =>
                      handleOnBlur('nomeMae', formControl.values.nomeMae)
                    }
                    onChange={e => {
                      setNomeMae(e.target.value)
                      setContaPj({
                        ...contaPj,
                        responsaveis: {
                          ...contaPj.responsaveis,
                          pessoas_fisicas: [
                            {
                              ...contaPj?.responsaveis?.pessoas_fisicas[0],
                              nome_mae: e.target.value,
                            } as any,
                          ],
                        },
                      })
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={5} lg={5}>
                  <Grid container alignItems="center">
                    <Checkbox
                      style={{ color: colors.green }}
                      checked={naoConstaNomeMae}
                      onChange={() => {
                        setNaoConstaNomeMae(!naoConstaNomeMae)
                        setNomeMae('')
                        setErrorMessageForm(errors => ({
                          ...errors,
                          nomeMae: '',
                        }))
                        formControl.setFieldValue('nomeMae', '')
                      }}
                    />
                    <Text>Não consta</Text>
                  </Grid>
                </Grid>
              </Grid>
              <Grid container spacing={2} style={{ margin: '8px 0' }}>
                <Grid item xs={12} sm={12} md={6} lg={6}>
                  <Input
                    placeholder="Email*"
                    name="email"
                    type="email"
                    value={email}
                    formControl={formControl}
                    onBlur={() =>
                      handleOnBlur('email', formControl.values.email)
                    }
                    errorMessage={errorMessageForm.email}
                    onChange={e => {
                      setEmail(e.target.value)
                      setContaPj({
                        ...contaPj,
                        responsaveis: {
                          ...contaPj.responsaveis,
                          pessoas_fisicas: [
                            {
                              ...contaPj?.responsaveis?.pessoas_fisicas[0],
                              email: e.target.value,
                            } as any,
                          ],
                        },
                      })
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={4} lg={4}>
                  <Input
                    placeholder="Celular*"
                    name="celular"
                    value={celular}
                    formControl={formControl}
                    mask={value => phoneMask(value)}
                    maxLength={15}
                    errorMessage={errorMessageForm.celular}
                    onBlur={() => {
                      formControl.setFieldValue('celular', phoneMask(celular))
                      handleOnBlur('celular', formControl.values.celular)
                    }}
                    onChange={e => {
                      setCelular(e.target.value)
                      setContaPj({
                        ...contaPj,
                        responsaveis: {
                          ...contaPj.responsaveis,
                          pessoas_fisicas: [
                            {
                              ...contaPj?.responsaveis?.pessoas_fisicas[0],
                              celular: e.target.value,
                            } as any,
                          ],
                        },
                      })
                    }}
                  />
                </Grid>
              </Grid>
              <Divider style={{ margin: '32px 0', width: '100%' }} />
              <Grid container spacing={2} style={{ margin: '8px 0' }}>
                <Grid container item xs={12} spacing={2}>
                  <Grid item xs={12} md={4} lg={4}>
                    <Input
                      placeholder="CEP*"
                      name="cep"
                      value={cep}
                      type="text"
                      formControl={formControl}
                      mask={value => cepMask(value)}
                      onChange={e => handleCep(e.target.value)}
                      maxLength={9}
                    />
                  </Grid>
                </Grid>
                <Grid container item xs={12} spacing={2}>
                  <Grid item xs={12} md={5} lg={5}>
                    <Input
                      placeholder="Endereço*"
                      name="logradouro"
                      formControl={formControl}
                      value={logradouro}
                      onChange={e => {
                        setLogradouro(e.target.value)
                        setContaPj({
                          ...contaPj,
                          responsaveis: {
                            ...contaPj.responsaveis,
                            pessoas_fisicas: [
                              {
                                ...contaPj?.responsaveis?.pessoas_fisicas[0],
                                logradouro: e.target.value,
                              } as any,
                            ],
                          },
                        })
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2} lg={2}>
                    <Input
                      placeholder="Número*"
                      name="numero"
                      formControl={formControl}
                      value={numero?.toString()}
                      onChange={e => {
                        setNumero(e.target.value)
                        setContaPj({
                          ...contaPj,
                          responsaveis: {
                            ...contaPj.responsaveis,
                            pessoas_fisicas: [
                              {
                                ...contaPj?.responsaveis?.pessoas_fisicas[0],
                                numero: e.target.value,
                              } as any,
                            ],
                          },
                        })
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid container item xs={12} spacing={2}>
                  <Grid item xs={12} md={4} lg={4}>
                    <Input
                      placeholder="Bairro*"
                      name="bairro"
                      formControl={formControl}
                      value={bairro}
                      onChange={e => {
                        setBairro(e.target.value)
                        setContaPj({
                          ...contaPj,
                          responsaveis: {
                            ...contaPj.responsaveis,
                            pessoas_fisicas: [
                              {
                                ...contaPj?.responsaveis?.pessoas_fisicas[0],
                                bairro: e.target.value,
                              } as any,
                            ],
                          },
                        })
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={5} lg={5}>
                    <Input
                      placeholder="Complemento"
                      name="complemento"
                      formControl={formControl}
                      value={complemento}
                      onChange={e => {
                        setComplemento(e.target.value)
                        setContaPj({
                          ...contaPj,
                          responsaveis: {
                            ...contaPj.responsaveis,
                            pessoas_fisicas: [
                              {
                                ...contaPj?.responsaveis?.pessoas_fisicas[0],
                                complemento: e.target.value,
                              } as any,
                            ],
                          },
                        })
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid container item xs={12} spacing={2}>
                  <Grid item xs={12} md={5} lg={5}>
                    <Input
                      placeholder="Cidade*"
                      name="cidade"
                      formControl={formControl}
                      disabled={true}
                      value={cidade}
                    />
                  </Grid>
                  <Grid item xs={12} md={3} lg={3}>
                    <Input
                      placeholder="Estado*"
                      name="estado"
                      formControl={formControl}
                      disabled={true}
                      value={estado}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Form>
      </Grid>
      <Divider style={{ margin: '32px 0' }} />

      <Grid container>
        <Grid item xs={12} sm={12} md={4} lg={4}>
          <Title>Envio de arquivos</Title>
          <Subtitle>Forneça a selfie e documentações do representante</Subtitle>
        </Grid>
        <Grid item xs={12} sm={12} md={8} lg={8}>
          <Grid>
            <Title>Selfie (Foto do rosto)</Title>
            <Subtitle>Envie a Selfie seguindo as dicas abaixo:</Subtitle>
            <Ul>
              <Li>Deixe o celular na altura dos olhos</Li>
              <Li> Procure lugares bem iluminados</Li>
              <Li>Evite fundos com estampa e uso de acessórios.</Li>
            </Ul>
            <ButtonFile
              id="selfie"
              label="Anexar documento*"
              onSelectFile={(file: File, fileName: string) =>
                handleSelectFile(
                  file,
                  fileName,
                  ETipoDocumentoOnboarding.SELFIE,
                )
              }
              file={getDocumentoNome(ETipoDocumentoOnboarding.SELFIE)}
              accept={'.jpeg, .png'}
            />
          </Grid>
          <Divider style={{ margin: '32px 0' }} />
          <Grid item>
            <Title>CNH</Title>
            <Subtitle>Envie a CNH seguindo as dicas abaixo:</Subtitle>
            <Ul>
              <Li>Tire a CNH do plástico, deixe aberta e na vertical</Li>
              <Li> Procure lugares bem iluminados e sem reflexo</Li>
              <Li>Evite fundos com estampa</Li>
              <Li>Envie o documento nos seguintes formatos: JPEG ou PNG</Li>
            </Ul>
            <ButtonFile
              id="cnh"
              label="Anexar documento*"
              onSelectFile={(file: File, fileName: string) =>
                handleSelectFile(file, fileName, ETipoDocumentoOnboarding.CNH)
              }
              file={getDocumentoNome(ETipoDocumentoOnboarding.CNH)}
              accept={'.jpeg, .png'}
            />
          </Grid>
          <Divider style={{ margin: '32px 0' }} />
          {relacao === EPerfil.RESPONSAVEL && (
            <Grid item>
              <Title>Carta de Procuração</Title>
              <Subtitle>Envie a Procuração seguindo as dicas abaixo:</Subtitle>
              <Ul>
                <Li>
                  Todas as páginas do documento devem estar em um único arquivo
                  PDF
                </Li>
                <Li>
                  {' '}
                  O documento deve estar registrado no órgão de registro
                  competente
                </Li>
              </Ul>
              <ButtonFile
                id="procuracao"
                label="Anexar documento*"
                onSelectFile={(file: File, fileName: string) =>
                  handleSelectFile(
                    file,
                    fileName,
                    ETipoDocumentoOnboarding.PROCURACAO,
                  )
                }
                file={getDocumentoNome(ETipoDocumentoOnboarding.PROCURACAO)}
                accept={'.pdf'}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid container>
        <Footer
          onClickBackButton={handleBackButton}
          onClickNextButton={handleSubmit}
          titleNextButton="Próximo"
          titleBackButton="Voltar"
          isNextButtonDisabled={!isOk}
        />
      </Grid>
      <SnackBar
        open={openErrorMessage}
        setOpen={setOpenErrorMessage}
        severity="error"
        message={errorMessage}
        horizontal="right"
      />
    </>
  )
}

export default Representante
